import branchesIcon from '@jetbrains/icons/branches'
import Link from '@jetbrains/ring-ui-built/components/link/link'
import {SvgIcon} from '@jetbrains/teamcity-ui/dist/components'
import classnames from 'classnames'
import type {SyntheticEvent} from 'react'
import * as React from 'react'

import EntityLink from '../../../containers/EntityLink'
import {useHash} from '../../../hooks/routes'
import {BuildTypePageTabNamesEnum, stringifyId} from '../../../types'
import {Modes} from '../../pages/BuildTypePage/BuildTypeOverviewTab/BuildTypeOverviewTab.modes'
import {trackBuildNavigationClick} from '../Builds/BuildDetails/BuildDetails.analyticEvents'
import CopyIconButton from '../CopyIconButton/CopyIconButton'
import MiddleEllipsis from '../MiddleEllipsis/MiddleEllipsis'
import RouterLink from '../RouterLink/RouterLink'
import Tooltip from '../Tooltip/Tooltip'
import {hasSelection} from '../Tree/Tree.utils'

import type {Props} from './BranchLabel.types'

import styles from './BranchLabel.module.css'

function BranchLabel(props: Props) {
  const {
    className,
    nameClassName,
    title,
    name,
    main,
    noLink,
    autoTrim,
    defaultTrim,
    withBorder,
    withTooltip,
    low,
    withIcon,
    inline,
    tailLength,
    href,
    to,
    buildId,
    pipelineId,
    branchNameInHref,
    isSakuraUI,
    buildTypeId,
    visibleNameClassName,
    ...restProps
  } = props
  const hash = useHash()
  const isExpanded = buildId != null && hash === stringifyId(buildId)
  const handleNavigationClick = React.useCallback(() => {
    trackBuildNavigationClick({buildId, targetType: 'other', isExpanded})
  }, [buildId, isExpanded])
  const onClick = React.useCallback(
    (event: SyntheticEvent) => {
      if (hasSelection()) {
        event.stopPropagation()
        event.preventDefault()
        return
      }
      handleNavigationClick()
    },
    [handleNavigationClick],
  )

  if (name == null) {
    return null
  }

  const classes = classnames(styles.buildBranch, className, {
    [styles.link]: !noLink,
    [styles.main]: main,
    [styles.autoTrim]: autoTrim,
    [styles.defaultTrim]: defaultTrim,
    [styles.withBorder]: withBorder,
    [styles.withIcon]: withIcon,
    [styles.low]: low,
    [styles.inline]: inline,
  })
  const commonProps = {
    ...restProps,
    className: classes,
    'data-test-branch-link': true,
    children: (
      <span className={styles.buildBranchContent} data-test="build-branch-content">
        {withIcon === true ? <SvgIcon icon={branchesIcon} className={styles.icon} /> : null}
        {withTooltip ? (
          <Tooltip long delay={500} title={name} className={styles.tooltipWrapper} selfOverflowOnly>
            <MiddleEllipsis
              title={title}
              tailLength={tailLength}
              className={nameClassName}
              visibleClassName={visibleNameClassName}
            >
              {name}
            </MiddleEllipsis>
          </Tooltip>
        ) : (
          <MiddleEllipsis
            title={title}
            tailLength={tailLength}
            className={nameClassName}
            visibleClassName={visibleNameClassName}
          >
            {name}
          </MiddleEllipsis>
        )}
        {inline === true ? (
          <CopyIconButton iconClassName={styles.copyIcon} text={name} title={'Copy to clipboard'} />
        ) : (
          <div className={styles.copyIconWrapper}>
            <CopyIconButton Component="div" text={name} title={'Copy to clipboard'} />
          </div>
        )}
      </span>
    ),
  }

  if (noLink === true) {
    return <span {...commonProps} />
  }

  if (to != null) {
    return <RouterLink to={to} {...commonProps} onClick={handleNavigationClick} />
  }

  if (isSakuraUI === true) {
    return (
      <EntityLink
        {...commonProps}
        buildTypeId={buildTypeId}
        pipelineId={pipelineId}
        params={() => ({
          branch: branchNameInHref,
          buildTypeTab: stringifyId(BuildTypePageTabNamesEnum.OVERVIEW),
          mode: Modes.BUILDS,
        })}
        draggable="false"
        onClick={onClick}
      />
    )
  }

  return <Link {...commonProps} href={href} onClick={handleNavigationClick} />
}

export default BranchLabel
